import React, {
    useState,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../utils/connect';
import {
    format,
    subDays,
    addWeeks,
    addDays,
    isBefore,
    parseISO,
    isValid,
} from 'date-fns';

import { isEmpty } from 'ramda';

import SlabNavContainer from '@app/ui/sports-components/SlabNavContainer';
import Container from '@app/ui/sports-components/AccordionContainer';
import List from '@app/ui/sports-components/AccordionList';
import Item from '@app/ui/sports-components/AccordionItem';
import Title from '@app/ui/sports-components/AccordionTitle';
import Icon from '@app/ui/sports-components/AccordionIcon';
import ChildItems from '@app/ui/sports-components/AccordionChildItems';
import { Button, Row, Col } from '@app/ui';
import classNames from '@app/ui/sports-components/utils/classNames';

import shortenArr from '../utils/shortenArr';
import { decimal, shortDate } from '../utils/formatters';
import { tooltipLabelCallback } from '../utils/charts/tooltip-label';

import PointChart from '../components/charts/PointChart';
import ChartLegend from './ChartLegend';
import { PacingContext } from './Context';
import { onTrack, offTrack, goalReached } from '../constants/pacingStatus';
// import { toPng } from 'html-to-image';

const legendElements = (currentColor, goalCompleteLabel, t) => {
    return [
        {
            color: '#FFCC00',
            label: t('pacing.visualization.legend.onTrackPace'),
            icon: 'solid-line',
        },
        {
            color: currentColor,
            label: t('pacing.visualization.legend.currentPace'),
            icon: 'dashed-line',
        },
        {
            color: currentColor,
            label: t('pacing.visualization.legend.currentValue'),
        },
        {
            color: '#666699',
            label: t('pacing.visualization.legend.completion', {
                goalCompleteLabel,
            }),
        },
        {
            color: '#0099CC',
            label: t('pacing.visualization.legend.forecastedFinalValue'),
        },
    ];
};

const PacingVisualization = ({ pacing }) => {
    const { t } = useTranslation('tools');
    const { goals } = pacing;
    const { currentGoalId } = useContext(PacingContext);

    const currentGoal = useMemo(
        () => goals.find(g => g.id === currentGoalId) || {},
        [goals, currentGoalId],
    );

    const hasCurrentGoal = currentGoal && !isEmpty(currentGoal);

    const {
        startDate,
        endDate,
        targetValue,
        currentValue,
        metricUpdatedAt,
        rightsholderName,
        sponsorName,
        placementNames,
        description,
        statusId,
    } = currentGoal;

    const formattedStartDate = startDate
        ? format(parseISO(startDate), 'yyyy-MM-dd')
        : null;
    const lowerDateLimit = startDate
        ? format(subDays(parseISO(startDate), 1), 'yyyy-MM-dd')
        : null;
    const formattedEndDate = endDate
        ? format(parseISO(endDate), 'yyyy-MM-dd')
        : null;
    const formattedMetricUpdated = metricUpdatedAt
        ? format(parseISO(metricUpdatedAt), 'yyyy-MM-dd')
        : null;

    const ref = useRef(null);
    const refComplete = useRef(null);

    const hasEnoughData = useMemo(() => {
        return currentValue / targetValue > 0.01 ? true : false;
    }, [currentValue, targetValue]);

    const isCurrentGoal = useMemo(() => {
        return [onTrack, offTrack, goalReached].includes(statusId);
    }, [statusId]);

    const shouldShowGraph = hasEnoughData && isCurrentGoal;

    // Open - Close container

    const [isExpanded, expand] = useState(false);
    const isLoading = false;
    const toggle = useCallback(() => {
        expand(!isExpanded);
    }, [isExpanded]);

    useEffect(() => {
        if (hasCurrentGoal && !isExpanded && shouldShowGraph) {
            toggle();
        }
    }, [hasCurrentGoal, currentGoal]);

    const visualizationClassName = classNames('-color-bg-white', {
        childOpen: isExpanded,
        childClose: !isExpanded,
    });

    const wrapperClassName = classNames(
        'gds-flex__item gds-flex__item--grow-0 -fix-insights__slab--item -color-bg-white',
        {
            wrapperEmpty: !shouldShowGraph,
            wrapperFilled: shouldShowGraph,
        },
    );

    const remainingDays = useMemo(() => {
        const value =
            parseInt(
                (new Date(endDate).getTime() - new Date().getTime()) /
                    (3600 * 1000 * 24),
            ) + 1;
        return value > 0 ? value : 0;
    }, [startDate, endDate]);

    const totalDays = useMemo(
        () =>
            parseInt(
                (new Date(endDate).getTime() - new Date(startDate).getTime()) /
                    (3600 * 1000 * 24),
            ) + 1,
        [startDate, endDate],
    );

    const title = useMemo(() => {
        if (!hasCurrentGoal) return null;
        const placementAppend = !isEmpty(placementNames)
            ? ` - ${shortenArr(placementNames, t('pacing.visualization.placements'))}`
            : '';
        return `${rightsholderName} & ${sponsorName}${placementAppend}`;
    }, [rightsholderName, sponsorName, placementNames, t]);

    // new metrics

    const elapsedDays =
        totalDays - remainingDays !== 0 ? totalDays - remainingDays : 1;

    const forecastedRemainingDays = currentValue
        ? Math.round(
              ((targetValue - currentValue) * elapsedDays) / currentValue,
          )
        : 'N/A';

    const forecastedCompleteDate =
        metricUpdatedAt &&
        isValid(new Date(metricUpdatedAt)) &&
        forecastedRemainingDays != 'N/A'
            ? format(
                  addDays(parseISO(metricUpdatedAt), forecastedRemainingDays),
                  'yyyy-MM-dd',
              )
            : null;

    const forecastedFinalValue =
        Math.round(currentValue / elapsedDays) * totalDays;

    const isForecastedCloser = useMemo(
        () =>
            forecastedCompleteDate
                ? isBefore(parseISO(forecastedCompleteDate), endDate)
                : false,
        [forecastedCompleteDate, endDate],
    );

    const upperDateLimit = isForecastedCloser
        ? endDate
            ? format(addWeeks(parseISO(endDate), 1), 'yyyy-MM-dd')
            : null
        : forecastedCompleteDate
          ? format(addWeeks(parseISO(forecastedCompleteDate), 1), 'yyyy-MM-dd')
          : null;

    // /* Downloads With Legend, slower, CORS errors because of css

    // const onClickDownload = useCallback(() => {
    //     if (!refComplete || !refComplete.current) return;

    //     toPng(refComplete.current)
    //         .then(dataUrl => {
    //             const a = document.createElement('a');
    //             a.href = dataUrl;
    //             a.setAttribute('target', '_blank');
    //             a.download = title ? `${title}.png` : 'image.png';

    //             // Adds support for firefox
    //             document.body.appendChild(a);

    //             a.click();
    //             a.remove();
    //         })
    //         .catch(err => {
    //             console.log(err);
    //         });
    // }, [title, refComplete]);

    /* Downloads only graph, faster, no errors */
    const onClickDownload = useCallback(() => {
        if (!ref || !ref.current) return;
        const canvas = ref.current.canvas;

        const imgUrl = canvas.toDataURL('image/png', 1.0);
        const a = document.createElement('a');
        a.href = imgUrl;
        a.setAttribute('target', '_blank');
        a.download = title ? `${title}.png` : 'image.png';

        // Adds support for firefox
        document.body.appendChild(a);

        a.click();
        a.remove();
    }, [title, ref]);

    const currentValuePoint = { x: formattedMetricUpdated, y: currentValue };
    const forecastedCompleteDatePoint = {
        x: forecastedCompleteDate,
        y: targetValue,
    };
    const forecastedFinalValuePoint = {
        x: formattedEndDate,
        y: forecastedFinalValue,
    };

    const currentPaceColor = isForecastedCloser ? '#3fae2a' : '#e5512e';
    const currentGoalCompleteLabel = useMemo(
        () => (statusId === 5 ? 'Goal' : 'Forecasted Goal'),
        [statusId],
    );

    const emptyGraphText =
        !hasEnoughData && isCurrentGoal
            ? t('pacing.visualization.empty.notEnoughProgress')
            : t('pacing.visualization.empty.pastGoals');

    const datasets = [
        {
            id: 0,
            color: [currentPaceColor, '#666699', '#0099CC'],
            pointStyle: ['circle', 'triangle', 'rectRot'],
            data: [
                currentValuePoint,
                forecastedCompleteDatePoint,
                forecastedFinalValuePoint,
            ],
        },
    ];

    const titleComponent = useMemo(() => {
        return (
            <div className="-text-center">
                <div className="gds-text--header-sm graphic-title">{title}</div>
                <div className="graphic-subtitle">
                    {shortDate(startDate)} - {shortDate(endDate)}
                </div>
            </div>
        );
    }, [startDate, endDate, title]);

    return (
        <Container className="-m-b-3">
            <List>
                <Item isActive={isExpanded}>
                    <Title
                        className="-text-tr-p -fix-primary-color"
                        context="secondary"
                        onClick={toggle}
                        size="sm"
                    >
                        {t('pacing.visualization.title')}
                    </Title>
                    <Icon
                        isWhite
                        size="sm"
                        style={{ cursor: 'pointer' }}
                        onClick={toggle}
                    />
                    <ChildItems size="sm" className={visualizationClassName}>
                        <SlabNavContainer
                            className={wrapperClassName}
                            vertical
                            justify
                        >
                            {shouldShowGraph && (
                                <div className="-p-v-2">
                                    <Button
                                        className="download-graph-button"
                                        onClick={onClickDownload}
                                        size="small"
                                        variant="primary"
                                    >
                                        <i className="fas fa-download -m-r-2" />
                                        {t('pacing.buttons.download')}
                                    </Button>
                                    {titleComponent}
                                </div>
                            )}
                            {shouldShowGraph && (
                                <div
                                    className="pacing-graph-container"
                                    ref={refComplete}
                                >
                                    <div
                                        className="pacing-graph gds-flex__item gds-flex gds-flex--justify-center gds-flex--content-center gds-flex--align-center -p-h-4 -p-v-2"
                                        data-test="pacing-graph"
                                    >
                                        <PointChart
                                            ref={ref}
                                            data={{
                                                datasets,
                                            }}
                                            options={{
                                                xType: 'date',
                                                yType: 'decimal',
                                                config: {
                                                    showLine: false,
                                                    responsive: true,
                                                    scales: {
                                                        x: {
                                                            type: 'time',
                                                            display: true,
                                                            time: {
                                                                unit: 'day',
                                                            },
                                                            min: lowerDateLimit,
                                                            max: upperDateLimit,
                                                        },
                                                        y: {
                                                            type: 'linear',
                                                            min: 0,
                                                            max:
                                                                Math.max(
                                                                    targetValue,
                                                                    currentValue,
                                                                    forecastedFinalValue,
                                                                ) * 1.2,
                                                        },
                                                    },
                                                    plugins: {
                                                        tooltip: {
                                                            callbacks: {
                                                                label: context =>
                                                                    tooltipLabelCallback(
                                                                        context,
                                                                        decimal,
                                                                        description.label,
                                                                        false,
                                                                        false,
                                                                        true,
                                                                        targetValue,
                                                                    ),
                                                            },
                                                        },
                                                        datalabels: {
                                                            display: false,
                                                        },
                                                        legend: {
                                                            display: false,
                                                        },
                                                        annotation: {
                                                            annotations: [
                                                                {
                                                                    type: 'line',
                                                                    xMin: formattedStartDate,
                                                                    xMax: isForecastedCloser
                                                                        ? forecastedFinalValuePoint.x
                                                                        : forecastedCompleteDatePoint.x,
                                                                    yMin: 0,
                                                                    yMax: isForecastedCloser
                                                                        ? forecastedFinalValuePoint.y
                                                                        : forecastedCompleteDatePoint.y,
                                                                    borderColor:
                                                                        currentPaceColor,
                                                                    borderDash:
                                                                        [
                                                                            10,
                                                                            10,
                                                                        ],
                                                                    drawTime:
                                                                        'beforeDraw',
                                                                },
                                                                {
                                                                    type: 'line',
                                                                    xMin: formattedStartDate,
                                                                    xMax: formattedEndDate,
                                                                    yMin: 0,
                                                                    yMax: targetValue,
                                                                    borderColor:
                                                                        'orange',
                                                                },
                                                                {
                                                                    type: 'line',
                                                                    xMin: formattedEndDate,
                                                                    xMax: formattedEndDate,
                                                                    borderColor:
                                                                        'gray',
                                                                    borderDash:
                                                                        [
                                                                            10,
                                                                            10,
                                                                        ],
                                                                    drawTime:
                                                                        'beforeDraw',
                                                                    label: {
                                                                        rotation:
                                                                            'auto',
                                                                        position:
                                                                            'end',
                                                                        content:
                                                                            t(
                                                                                'pacing.visualization.annotations.goalEndDate',
                                                                            ),
                                                                        enabled: true,
                                                                    },
                                                                },
                                                                {
                                                                    type: 'line',
                                                                    yMin: targetValue,
                                                                    yMax: targetValue,
                                                                    borderColor:
                                                                        'gray',
                                                                    borderDash:
                                                                        [
                                                                            10,
                                                                            10,
                                                                        ],
                                                                    drawTime:
                                                                        'beforeDraw',
                                                                    label: {
                                                                        content:
                                                                            t(
                                                                                'pacing.visualization.annotations.targetValue',
                                                                            ),
                                                                        enabled: true,
                                                                        position:
                                                                            'start',
                                                                    },
                                                                },
                                                            ],
                                                        },
                                                    },
                                                },
                                            }}
                                            isLoading={isLoading}
                                        />
                                    </div>
                                    <div className="gds-flex__item gds-flex gds-flex--justify-center gds-flex--content-center gds-flex--align-center -p-h-4 -p-v-2">
                                        <ChartLegend
                                            legendElements={legendElements(
                                                currentPaceColor,
                                                currentGoalCompleteLabel,
                                                t,
                                            )}
                                        />
                                    </div>
                                </div>
                            )}
                            {!shouldShowGraph && (
                                <div>
                                    <Row
                                        gutter={16}
                                        className="-m-t-6 -p-t-6"
                                        style={{ height: '80px' }}
                                    >
                                        <Col span={24}>
                                            <p className="-color-tx-war-lt-3 pacing-graph-empty">
                                                <i className="fas fa-exclamation-triangle" />
                                            </p>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={24}>
                                            <p className="gds-text--italic -p-t-3 -m-b-1 pacing-graph-empty">
                                                {emptyGraphText}
                                            </p>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </SlabNavContainer>
                    </ChildItems>
                </Item>
            </List>
        </Container>
    );
};

PacingVisualization.propTypes = {
    pacing: PropTypes.object,
};

export default connect(['client', 'pacing'])(PacingVisualization);
